import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit, FaMoneyBill } from "react-icons/fa";
import { fetchData } from "../../helpers/fetchData";
import NewSettingsTable from "./NewSettingsTable";
import { useNavigate, useLocation } from "react-router-dom";
import i18next from "i18next";
import CustomTabs from "../Shared/CustomTabs";

function NewSettingsList() {
  const { t } = useTranslation();
  const lang = i18next.language;
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [listData, setListData] = useState([]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const res = await fetchData("POST", "/tsetting/list");
      setListData(res?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Group data by title type
  const groupedData = useMemo(() => {
    const payerSettings = listData.filter(
      (item) => item?.title === "payer_setting"
    );
    const paymentSettings = listData.filter(
      (item) => item?.title === "payment_setting"
    );
    const otherSettings = listData.filter(
      (item) =>
        item?.title !== "payer_setting" && item?.title !== "payment_setting"
    );

    const groups = [];
    if (payerSettings.length > 0) {
      groups.push({
        type: "group",
        title: "payer_settings",
        label: t("Payer Settings"),
        items: payerSettings,
      });
    }
    if (paymentSettings.length > 0) {
      groups.push({
        type: "group",
        title: "payment_settings",
        label: t("Payment Settings"),
        items: paymentSettings,
      });
    }
    otherSettings.forEach((item) => {
      groups.push({
        type: "single",
        title: item?.title,
        data: item,
      });
    });

    return groups;
  }, [listData, t]);

  const getTitle = (key) => {
    const titles = {
      cash_invoices: "Cash Invoices",
      insurance_invoices: "Insurance Invoices",
      credit_invoices: "Credit Invoices",
      purchase_entries: "Purchase Entries",
      payrolls: "Payrolls",
    };

    return (
      titles[key] ||
      key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    );
  };

  useEffect(() => {
    fetchInitialData();
  }, [location.key]);

  const tabs = useMemo(() => {
    return groupedData.map((group) => {
      if (group.type === "single") {
        const item = group.data;
        return {
          name: getTitle(item?.title),
          content: (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 p-1.5 bg-gray-100 rounded-lg text-white">
                    <FaMoneyBill size={20} color="#0070C2" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 capitalize">
                    {t(getTitle(item?.title))}
                  </h3>
                </div>
                <button
                  className="flex-shrink-0 p-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-lg text-white"
                  onClick={() => {
                    if (item.title === "his_transactions_settings") {
                      navigate("/manager/settings/gl/create", {
                        state: { editData: item },
                      });
                    } else {
                      navigate("/manager/settings/create", {
                        state: { editData: item },
                      });
                    }
                  }}
                  aria-label="Edit Setting"
                >
                  <FaEdit size={20} color="#0070C2" />
                </button>
              </div>
              <NewSettingsTable data={item?.transactions} title={item?.title} />
            </div>
          ),
        };
      } else {
        return {
          name: group.title === "payer_settings" ? "Payer Settings" : "Payment Settings",
          content: (
            <div className="flex flex-col gap-4 mt-4">
              {group.items.map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  className=""
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-semibold text-gray-700">
                      {group.title === "payer_settings"
                        ? item?.payer_name || t("Payer Setting")
                        : (lang === "ar"
                          ? item?.payment_name_ar
                          : item?.payment_name_en) || t("Payment Setting")}
                    </h4>
                    <FaEdit
                      size={18}
                      color="#0070C2"
                      className="cursor-pointer hover:opacity-70"
                      onClick={() => {
                        if (group.title === "payer_settings") {
                          navigate("/manager/settings/payer/create", {
                            state: { editData: item },
                          });
                        } else if (group.title === "his_transactions_settings") {
                          navigate("/manager/settings/gl/create", {
                            state: { editData: item },
                          });
                        } else {
                          navigate("/manager/settings/payment/create", {
                            state: { editData: item },
                          });
                        }
                      }}
                    />
                  </div>
                  <NewSettingsTable data={item?.transactions} title={item?.title} />
                </div>
              ))}
            </div>
          ),
        };
      }
    });
  }, [groupedData, lang, navigate, t]);

  if (listData.length < 1) {
    return (
      <div className="flex w-full items-center justify-center border h-20">
        <h1>{t("No Data Found")}</h1>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <CustomTabs tabs={tabs} />
    </div>
  );
}

export default NewSettingsList;

